import type { PaigeClient } from '@paige/api-client';
import type { InsightsApi } from './insights-api';
import type { CustomersApi } from './customers-api';
import type { ProblemsApi, WorkApi } from './problems-api';
import type { TimelineApi } from './timeline-api';
import type { SupportFixesApi } from './support-fixes-api';
import type { PeriodApi } from './period-api';
import type { PickerCompany } from '@paige/admin';
import { createContext, useContext } from 'react';
import type { CompanyApi } from './company-api';
import type { PartnerApi } from './partner-api';
import type { CompanyLedgerApi, PartnerAccountsApi } from './partner-ledger-api';
import type { CreditAlertsApi } from './credit-alerts-api';
import type { ApiKeysApi } from './api-keys-api';
import type { DevicesApi } from './devices-api';
import type { MobileApi } from './mobile-api';
import type { LeadsApi } from './leads-api';
import type { NotificationsApi } from './notifications-api';
import type { Acquisition } from '../analytics/acquisition';
import type { EventSourceFactory } from '@paige/live';
import type {
  AdminFlow,
  FlowItemsPage,
  FlowIncident,
  FlowIncidents,
  FlowIncidentCompaniesPage,
  FlowHistory,
  SupportFiles,
  FlowItemsQuery,
  Customer,
  CustomerAction,
  ExceptionRow,
  Fleet,
  InboundMessage,
  IntakeSource,
  Job,
  JobDetail,
  JobState,
  AdminPools,
  AdminHealth,
  AdminGpuCards,
  AdminDesktopGpus,
  DesktopPolicyAnswer,
  DesktopPolicyBody,
  DesktopPolicyState,
  Principal,
  CampaignSpend,
  TenantAnalytics,
  SourceInput,
  SupportAccessGrant,
  SupportAccessRequest,
  MyInvitation,
  PersonAppearance,
  Engagement,
} from './types';

/**
 * Everything the screens read or change, behind one interface, grouped by area so no surface
 * grows past twelve members. `api-data-source.ts` implements it over `@paige/api-client`;
 * tenant-scoped calls take the tenant id first and send it as `Paige-Tenant`.
 */
export interface DataSource {
  session: SessionApi;
  /** The person's own look of Paige: one accent colour and Show tooltips, in every company. */
  appearance: AppearanceApi;
  engagement: EngagementApi;
  fleet: FleetApi;
  company: CompanyApi;
  partner: PartnerApi;
  /** Partner billing on Ademero's invoice (ADR-0036): a company's pause, quotes and card move, staff only. */
  companyLedger: CompanyLedgerApi;
  /** The partner account page: a paying account's channel, standing, companies and statements, staff only. */
  partnerAccounts: PartnerAccountsApi;
  alerts: CreditAlertsApi;
  keys: ApiKeysApi;
  devices: DevicesApi;
  /** Paige Mobile: the company's senders, the app's admin sign-in, the invite page's hand-off. */
  mobile: MobileApi;
  leads: LeadsApi;
  notifications: NotificationsApi;
  /** The typed client itself, for the product event stream's batches. */
  client: PaigeClient;
  admin: AdminApi;
  /** Opens the live event stream (`GET /events`, SSE). */
  createEventSource: EventSourceFactory;
  eventsUrl(tenant: string): string;
}

export interface SessionApi {
  getMe(): Promise<Principal>;
  /**
   * `emailCode`: the code Paige mailed, when the sign-in was refused for an address nobody had proven.
   * `invitation`: the company whose invitation's link this sign-up came from (`/sign-up?invite=`).
   */
  create(idToken: string, emailCode?: string, invitation?: string): Promise<Principal>;
  /** The invitations waiting for the signed-in person: nobody joins a company without saying so. */
  invitations(): Promise<MyInvitation[]>;
  /** Accepts one; the answer is the principal with the company among its tenants. */
  acceptInvitation(id: string): Promise<Principal>;
  declineInvitation(id: string): Promise<void>;
  /** Mails the six-digit code to the address the token names. */
  sendEmailCode(idToken: string): Promise<void>;
  /** Confirms the token's address with the mailed code, signing nobody in (two-step sign-in needs it). */
  confirmEmail(idToken: string, emailCode: string): Promise<void>;
  /** Asks for the link to choose a new password; the answer is the same whatever the address. */
  requestPasswordReset(email: string): Promise<void>;
  /** Sets the new password with the token the mailed link carries; signs nobody in. */
  resetPassword(token: string, password: string): Promise<void>;
  /** Names the company: the company is created on Free, the person is its admin, the session is set. */
  /** `acquisition` is where the browser first came from (R9 a), or null for a direct visit. */
  signUp(
    idToken: string,
    company: string,
    acquisition?: Acquisition | null,
    partnerCode?: string | null,
  ): Promise<Principal>;
  destroy(): Promise<void>;
}

/** `/me/engagement`: the person's record in a company; the dashboard closes its one-time pointers there. */
export interface EngagementApi {
  get(tenant: string): Promise<Engagement>;
  /** Adds one hint to the closed ones, keeping the rest and the Show hints switch; answers the record. */
  closeHint(tenant: string, id: string): Promise<Engagement>;
}

/** `/me/appearance`: the person's accent colours (null follows Paige blue) and tooltips, replaced whole. */
export interface AppearanceApi {
  get(): Promise<PersonAppearance>;
  save(appearance: PersonAppearance): Promise<PersonAppearance>;
}

export interface FleetApi {
  getFleet(tenant: string): Promise<Fleet>;
  getJob(tenant: string, jobId: string): Promise<JobDetail>;
  setJobState(tenant: string, jobId: string, state: JobState): Promise<JobDetail>;
  createJob(tenant: string, name: string): Promise<Job>;
  /** A manager's: the job under a new name; refused 409 when another job of the company has it. */
  renameJob(tenant: string, jobId: string, name: string): Promise<Job>;
  /** An admin's: the job, its documents, exports and learning removed; nothing refunded. */
  deleteJob(tenant: string, jobId: string): Promise<void>;
  /** The job's sources; reading makes the email source for a job from before addresses existed. */
  listSources(tenant: string, jobId: string): Promise<IntakeSource[]>;
  addSource(tenant: string, jobId: string, input: SourceInput): Promise<IntakeSource>;
  updateSource(
    tenant: string,
    jobId: string,
    sourceId: string,
    input: SourceInput,
  ): Promise<IntakeSource>;
  releaseSource(tenant: string, jobId: string, sourceId: string): Promise<IntakeSource>;
  /** An email source's recent messages, newest first. */
  listSourceMessages(tenant: string, jobId: string, sourceId: string): Promise<InboundMessage[]>;
  retryDestination(tenant: string, jobId: string, destinationId: string): Promise<void>;
  listExceptions(tenant: string, jobId: string): Promise<ExceptionRow[]>;
}

export type { FunnelQuery, PeriodQuery, RevenueQuery, UsageQuery } from './period-api';

export interface AdminApi
  extends InsightsApi, CustomersApi, ProblemsApi, WorkApi, TimelineApi, SupportFixesApi, PeriodApi {
  getCustomer(tenantId: string): Promise<Customer>;
  customerAction(tenantId: string, action: CustomerAction): Promise<Customer>;
  /** Opens a company as support: a recorded, time-boxed grant (§14.4); staff bind the tenant only under one. */
  grantAccess(tenantId: string, request: SupportAccessRequest): Promise<SupportAccessGrant>;
  /** Ends this staff member's own live grants on the company (the in-company banner's End); audited. */
  endAccess(tenantId: string): Promise<void>;
  /** Stages and queues, live: every company, or one by slug or id. */
  getFlow(company?: string): Promise<AdminFlow>;
  /** The items behind a count on Stages and queues, a page at a time. */
  listFlowItems(query: FlowItemsQuery): Promise<FlowItemsPage>;
  /** What is not simply flowing, one row a cause, worst first: every company, or one. */
  listFlowIncidents(company?: string): Promise<FlowIncidents>;
  /** One cause's companies, largest first, five at a time. */
  listFlowIncidentCompanies(
    incident: FlowIncident,
    company: string | null,
    offset: number,
  ): Promise<FlowIncidentCompaniesPage>;
  /** Stages and queues' last hour, a recorded minute at a time, for the scrubber. */
  getFlowHistory(minutes: number): Promise<FlowHistory>;
  /** A batch's files and pages, under the support grant on its company (`Paige-Tenant`). */
  listBatchFiles(tenantId: string, batchId: string): Promise<SupportFiles>;
  /** The company picker's search: at most twenty, the newest when nothing is typed. */
  searchCompanies(q: string): Promise<PickerCompany[]>;
  /** Pools and releases: what is deployed, as the health monitor last reported it. */
  getPools(): Promise<AdminPools>;
  /** Health: what the health monitor knows, from its own records. */
  getHealth(): Promise<AdminHealth>;
  /** Which GPU does what: desktop cards, cloud pools, each model's desk and cloud split. */
  getGpuCards(): Promise<AdminGpuCards>;
  /** Desktop GPUs: every desktop worker of the last 30 days and the machines enrolled that never connected. */
  getDesktopGpus(): Promise<AdminDesktopGpus>;
  /** Names a desktop GPU the way people know it; an empty name clears it. */
  nameDesktopGpu(worker: string, name: string): Promise<void>;
  /** A desktop machine's cards, schedule and pause: the newest copy, and what each card does now. */
  getDesktopGpuPolicy(machine: string): Promise<DesktopPolicyState>;
  /** Changes a desktop machine's cards, schedule and pause; sent to the machine, or waiting for it. */
  setDesktopGpuPolicy(machine: string, body: DesktopPolicyBody): Promise<DesktopPolicyAnswer>;
  enterCampaignSpend(entry: CampaignSpend): Promise<void>;
  getTenantAnalytics(tenantId: string): Promise<TenantAnalytics>;
}

const DataSourceContext = createContext<DataSource | null>(null);
export const DataSourceProvider = DataSourceContext.Provider;

export function useDataSource(): DataSource {
  const ds = useContext(DataSourceContext);
  if (!ds) throw new Error('useDataSource must be used inside <DataSourceProvider>');
  return ds;
}
